import type { Article } from "../../../types/blog";

const article: Article = {
  slug: "ecran-telephone-casse",
  silo: "telephone",
  isPillar: true,
  h1: "Écran de téléphone cassé : que faire et faut-il le réparer ?",
  metaTitle: "Écran de téléphone cassé : que faire ?",
  metaDescription: "Écran fissuré ou brisé sur votre smartphone ? Découvrez les risques d'attendre, les options de réparation et comment éviter la casse à l'avenir.",
  excerpt: "Une chute, un choc, et voilà l'écran de votre téléphone fissuré ou brisé. Avant de paniquer, voici ce qu'il faut savoir pour agir sans aggraver les dégâts.",
  publishedAt: "2026-08-10",
  readingMinutes: 7,
  relatedSlugs: ["telephone-ne-s-allume-plus", "telephone-tombe-dans-eau", "batterie-telephone-se-decharge-vite", "telephone-ne-charge-plus"],
  faq: [
    {
      question: "Peut-on continuer à utiliser un téléphone avec l'écran cassé ?",
      answer: "C'est possible tant que l'écran reste tactile et lisible, mais ce n'est pas recommandé. Les fissures s'agrandissent avec le temps, des éclats de verre peuvent blesser les doigts, et l'humidité ou la poussière peuvent s'infiltrer et endommager l'électronique interne.",
    },
    {
      question: "Combien coûte la réparation d'un écran de téléphone ?",
      answer: "Le prix varie fortement selon le modèle, la marque et le type de dégât (vitre seule ou dalle complète touchée). Un diagnostic précis par un technicien permet d'obtenir un devis fiable avant toute intervention.",
    },
    {
      question: "Vaut-il mieux réparer l'écran ou racheter un téléphone neuf ?",
      answer: "Cela dépend de l'âge et de l'état général du téléphone. Si le reste de l'appareil fonctionne bien et que le modèle est récent, la réparation de l'écran est presque toujours plus économique qu'un rachat.",
    },
    {
      question: "Un écran fissuré peut-il endommager d'autres composants ?",
      answer: "Oui. Une fissure laisse passer poussière et humidité vers les composants internes, ce qui peut affecter le tactile, la luminosité ou même provoquer des courts-circuits si le téléphone est exposé à l'eau par la suite.",
    },
  ],
  content: [
    {
      type: "p",
      text: "Un écran de téléphone cassé est l'une des pannes les plus fréquentes que nos techniciens rencontrent. Qu'il s'agisse d'une simple fissure dans un coin ou d'un écran complètement brisé, la question qui revient toujours est la même : faut-il agir tout de suite, et comment ?",
    },
    {
      type: "h2",
      text: "Les différents types de casse d'écran",
    },
    {
      type: "p",
      text: "Tous les écrans cassés ne se valent pas. Il est utile de distinguer plusieurs cas de figure avant d'envisager une réparation.",
    },
    {
      type: "ul",
      items: [
        "Vitre fissurée mais tactile et affichage intacts : dégât esthétique surtout, mais qui s'aggrave avec le temps",
        "Vitre brisée avec éclats visibles : risque de coupure au contact des doigts",
        "Taches noires ou lignes colorées à l'écran : la dalle elle-même est touchée, pas seulement la vitre",
        "Écran qui ne répond plus au tactile par endroits : le circuit tactile a été endommagé par le choc",
        "Écran totalement noir malgré un téléphone qui vibre ou sonne : la dalle ou sa connexion est hors service",
      ],
    },
    {
      type: "h2",
      text: "Pourquoi il ne faut pas trop attendre",
    },
    {
      type: "p",
      text: "Un écran fissuré peut sembler être un simple problème esthétique, mais il expose votre téléphone à des risques bien plus importants avec le temps.",
    },
    {
      type: "ul",
      items: [
        "Infiltration de poussière et d'humidité par les micro-fissures, pouvant atteindre les composants internes",
        "Aggravation progressive de la casse à chaque manipulation ou pression sur l'écran",
        "Risque de coupure avec des éclats de verre qui se détachent",
        "Perte de sensibilité tactile qui rend certaines zones de l'écran inutilisables",
        "Si le téléphone tombe ensuite [dans l'eau](slug:telephone-tombe-dans-eau), les fissures existantes aggravent fortement les dégâts internes",
      ],
    },
    {
      type: "callout",
      variant: "warning",
      title: "Ne tardez pas si des éclats se détachent",
      text: "Si des morceaux de verre se détachent de l'écran, arrêtez d'utiliser le téléphone normalement et faites-le examiner rapidement. Le risque de coupure est réel, surtout pour les enfants qui pourraient utiliser l'appareil.",
    },
    {
      type: "h2",
      text: "Réparation ou remplacement : comment choisir ?",
    },
    {
      type: "p",
      text: "Dans la grande majorité des cas, remplacer uniquement l'écran (vitre et dalle) est possible et bien plus économique qu'un remplacement complet de l'appareil. Le coût dépend de plusieurs facteurs : le modèle du téléphone, la disponibilité de la pièce d'origine, et l'étendue réelle des dégâts.",
    },
    {
      type: "p",
      text: "En ordre de grandeur, une réparation d'écran reste généralement bien plus abordable qu'un téléphone neuf, sauf sur des modèles très haut de gamme où la pièce détachée est elle-même coûteuse. Un diagnostic permet de vous donner un chiffrage précis avant toute intervention, sans mauvaise surprise.",
    },
    {
      type: "p",
      text: "Si votre téléphone présente d'autres signes de faiblesse en parallèle, comme une [batterie qui se décharge anormalement vite](slug:batterie-telephone-se-decharge-vite), il peut être pertinent de faire réparer les deux problèmes en même temps pour éviter une nouvelle intervention peu après.",
    },
    {
      type: "h2",
      text: "Comment éviter la casse d'écran à l'avenir",
    },
    {
      type: "ol",
      items: [
        "Utiliser une coque avec bords surélevés qui protège l'écran en cas de chute face contre le sol",
        "Poser un verre trempé ou un film de protection résistant aux chocs et aux rayures",
        "Éviter de poser le téléphone dans une poche avec des clés ou d'autres objets durs",
        "Ne pas laisser le téléphone au bord d'une table ou d'un canapé",
        "Faire attention en manipulant le téléphone à proximité de surfaces dures comme le carrelage",
      ],
    },
    {
      type: "callout",
      variant: "tip",
      title: "Le duo coque + verre trempé",
      text: "La combinaison d'une coque avec bords surélevés et d'un verre trempé de qualité réduit très fortement le risque de casse lors d'une chute, même sur un sol dur.",
    },
    {
      type: "h2",
      text: "Ce que nos techniciens vérifient avant réparation",
    },
    {
      type: "p",
      text: "Avant de remplacer un écran, nos techniciens réalisent un diagnostic complet de l'appareil : tactile, luminosité, capteurs de proximité, caméra frontale et connectique interne. Cela permet de s'assurer qu'aucun autre problème n'est passé inaperçu, notamment si le téléphone présente aussi des soucis pour [démarrer](slug:telephone-ne-s-allume-plus) ou pour [charger correctement](slug:telephone-ne-charge-plus). Une fois la réparation effectuée, l'écran est testé dans toutes ses fonctions avant restitution.",
    },
  ],
};

export default article;
